import { Component, DestroyRef, Input, model, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ForecastService } from '../../../shared/services/forecast.service';
import { ReplPlanSpecLine } from '../../../shared/models/forecast/specification/replPlanSpecLine';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { SharedModule } from '../../../shared/shared.module';
import { ReplPlan } from '../../../shared/models/forecast/replPlan';
import { IExpandable } from '../../../shared/models/forecast/iExpandable';
import { NoScrollDirective } from '../../../directives/nx-no-scroll.directive';
import { Alert, AlertService, AlertType } from '@michelin/theme';
import { IModif } from '../../../shared/models/IModif';
import { ContextService } from '../../../shared/services/context.service';
import { NavigationConfirmService } from '../../../shared/services/navigation-confirm.service';
import { ReplPlanSpecColumn } from '../../../shared/models/forecast/specification/replPlanSpecColumn';
import {NgxMaskDirective, provideNgxMask} from "ngx-mask";

@Component({
  selector: 'mic-forecast-table',
  standalone: true,
  imports: [CommonModule, SharedModule, TranslateModule, NoScrollDirective, NgxMaskDirective],
  providers: [provideNgxMask()],
  templateUrl: './forecast-table.component.html',
  styleUrls: ['./forecast-table.component.scss'],
  animations: [
    trigger('expandTrigger', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '60px' })),
      transition('expanded <=> collapsed', animate('300ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ]
})
export class ForecastTableComponent implements OnChanges, IModif, OnDestroy {
  /**
   * Constant used to detect positive infinity
   */
  protected readonly Number = Number;

  /**
   * Screen's data
   * @private
   */
  protected pendingRepPlan: ReplPlan;

  /**
   * Datatable config
   */
  currentYear: number = new Date().getFullYear();
  currentMonth = new Date().getMonth() + 1;
  monthsNumbersToDisplay = Array.from({ length: 12 }, (_, i) => ((this.currentMonth - 1 + i) % 12) + 1);
  columns = ['sizes', ...this.monthsNumbersToDisplay.map((m) => m.toString()), 'action'];
  form: FormArray<FormGroup> = new FormArray<FormGroup>([]);

  /**
   * Search parameters (two-way bound in case of rollback with confirm modal)
   */
  @Input() siteId: number;
  @Input() size: string;
  year = model<number>(new Date().getFullYear());
  private isRollingBackSearchParams = false;

  readonly digitMask = '0*';
  readonly digitPatterns = { '0': { pattern: /\d/ } };

  /**
   * Loading status
   */
  isLoadingData = model<boolean>(false);
  isSavingData = model<boolean>(false);

  /**
   * Pending status
   */
  isModificationPending = model<boolean>(false);

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly forecastService: ForecastService,
    private readonly fb: FormBuilder,
    private readonly alertService: AlertService,
    private readonly translate: TranslateService,
    private readonly contextService: ContextService,
    private readonly navConfirmService: NavigationConfirmService
  ) {
    this.contextService.register(this);
  }

  getModifStatus(): boolean {
    return this.isModificationPending();
  }

  ngOnDestroy(): void {
    this.contextService.unRegister(this);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['size']) {
      this.loadRepPlanData();
    }
    if (changes.year) {
      if (this.isRollingBackSearchParams) {
        this.isRollingBackSearchParams = false;
        return;
      }
      this.navConfirmService.beforeActionWithConfirmation(
        this.getModifStatus(),
        () => {
          this.loadRepPlanData();
        },
        () => {
          this.isRollingBackSearchParams = true;
          this.year.update(() => changes.year.previousValue);
        }
      );
    }
  }

  /**
   * Regenerate a new rep plan and override pending data with generated rep plan
   */
  public resetModifications() {
    this.loadRepPlanData();
  }

  /**
   * Fetch rep plan from the backend
   * @private
   */
  private loadRepPlanData() {
    if (!this.siteId || !this.size) return;
    this.isLoadingData.update(() => true);
    this.isModificationPending.update(() => false);

    const expandedSpecs = new Set<number>();
    if (this.pendingRepPlan?.replPlanSpecLines) {
      for (const specLine of this.pendingRepPlan.replPlanSpecLines) {
        if (specLine.expanded === 'expanded') expandedSpecs.add(specLine.tireSpecPk);
      }
    }

    this.forecastService
      .getReplPlan(1, this.siteId, this.size, this.year(), this.currentMonth)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.isLoadingData.update(() => false);
        this.processReplPlanForTable(result);

        if (this.pendingRepPlan) {
          this.pendingRepPlan.totalLine.expanded = 'expanded';
          for (const specLine of this.pendingRepPlan.replPlanSpecLines) {
            specLine.expanded = expandedSpecs.has(specLine.tireSpecPk) ? 'expanded' : 'collapsed';
          }
        }
      });
  }

  protected isTotalExpanded(): boolean {
    // Truthy value -> True / Falsy value (null or undefined) -> False
    return !!this.pendingRepPlan && this.pendingRepPlan.totalLine?.expanded === 'expanded';
  }

  /**
   * Ask the backend to recalculate rep plan, next update table
   * @private
   */
  private reCalculateRepPlan() {
    this.isModificationPending.update(() => true);
    this.forecastService
      .reCalculateReplPlan(1, this.siteId, this.size, this.pendingRepPlan)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => this.restoreDisplayStatusAndPendingData(result));
  }

  /**
   * Copy all proposed replenishments and recalculate month per month the next right proposed replenishment to apply to follow param rules
   * @private
   */
  public copyAllProposedReplAndRecalculate() {
    this.isModificationPending.update(() => true);
    this.forecastService
      .copyAllProposedRepl(1, this.siteId, this.size, this.pendingRepPlan)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => this.restoreDisplayStatusAndPendingData(result));
  }

  /**
   * Copy one spec proposed replenishments and recalculate month per month the next right proposed replenishment to apply to follow param rules
   * @private
   */
  protected copyOneSpecProposedReplAndRecalculate(specToCopy: number) {
    this.isModificationPending.update(() => true);
    this.forecastService
      .copyOneSpecProposedRepl(1, this.siteId, specToCopy, this.size, this.pendingRepPlan)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => this.restoreDisplayStatusAndPendingData(result));
  }

  /**
   * Save a Replenishment Plan for given parameters
   */
  public saveReplPlan() {
    this.isSavingData.update(() => true);
    this.forecastService
      .saveReplPlan(1, this.siteId, this.size, this.pendingRepPlan)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.alertService.showAlert({
            type: AlertType.success,
            title: `${this.translate.instant('forecast.save.successTitle')} ${this.size}`
          } as Alert);
          this.isSavingData.update(() => false);
          this.isModificationPending.update(() => false);
          this.loadRepPlanData();
        },
        error: (err) => {
          let errorMsg: string | undefined = undefined;
          if (err.error && err.error.message) {
            errorMsg = `(${err.error.message})`;
          }
          this.alertService.showAlert({
            type: AlertType.critical,
            title: `${this.translate.instant('forecast.save.errorTitle')} ${this.size}`,
            content: `${this.translate.instant('forecast.save.errorMsg')} ${errorMsg}`
          } as Alert);
          this.isSavingData.update(() => false);
        }
      });
  }

  /**
   * Process the replPlan data to be usable by the table UI
   * - Calculate frozen zone
   * - Assign data to datasource
   * - Rebuild the formControls linked to this data
   * @param data the data to process
   * @private
   */
  private processReplPlanForTable(data: ReplPlan) {
    this.pendingRepPlan = data;
    if (!this.pendingRepPlan) {
      return;
    }
    for (const specLine of data.replPlanSpecLines) {
      let frozenZone: number = data.param.frozenZone;
      if (!frozenZone) frozenZone = 0;
      // Frozen period is displayed only from current date to from current + frozen
      if (this.year() === this.currentYear) {
        for (const month of this.monthsNumbersToDisplay) {
          const col = specLine.columns?.[month];
          if (!col) continue;
          frozenZone -= 1;
          col.frozen = frozenZone >= 0;
        }
      }
      specLine.expanded = 'collapsed';
    }
    data.totalLine.expanded = 'expanded';
    // Build form after data is set
    this.buildForm();
  }

  /**
   * Build a FormArray where each FormGroup corresponds to a row (ForecastRepPlan)
   * and contains two controls per month: confirmedFitment_{month} and confirmedRepl_{month}.
   */
  private buildForm(): void {
    const groups = this.pendingRepPlan.replPlanSpecLines.map((row: ReplPlanSpecLine) => {
      const controls: { [key: string]: FormControl } = {};
      this.monthsNumbersToDisplay.forEach((month: number) => {
        const col = row.columns?.[month];
        // Default to 0 when no value present
        controls[`confirmedFitment_${month}`] = new FormControl<number>(col?.confirmedFitment ?? 0, [Validators.min(0), Validators.max(999)]);
        controls[`confirmedRepl_${month}`] = new FormControl<number>(col?.confirmedRepl ?? 0, [Validators.min(0), Validators.max(999)]);
      });
      return this.fb.group(controls) as FormGroup;
    });

    this.form = this.fb.array(groups) as FormArray<FormGroup>;
  }

  /**
   * Utility to retrieve the FormControl for a given row and month
   */
  getControl(element: ReplPlanSpecLine, month: number, field: 'confirmedFitment' | 'confirmedRepl'): FormControl {
    // Find the row index from the datasource so the control is stable across change detection cycles
    const rowIndex = this.pendingRepPlan.replPlanSpecLines.indexOf(element);
    if (rowIndex === -1 || !this.form || !this.form.at(rowIndex)) {
      // Fallback to a standalone control (shouldn't happen after buildForm)
      return new FormControl(null);
    }

    const group = this.form.at(rowIndex) as FormGroup;
    const controlName = `${field}_${month}`;
    let control = group.get(controlName) as FormControl | null;
    if (!control) {
      // If missing, create and add to the group so it's persisted (default 0)
      control = new FormControl<number>(0, [Validators.min(0), Validators.max(999)]);
      group.addControl(controlName, control);
    }
    return control as FormControl;
  }

  /**
   * Called on input change: if control is empty, coerce to 0 and update data source.
   */
  onChange(element: ReplPlanSpecLine, month: number, field: 'confirmedFitment' | 'confirmedRepl'): void {
    const rowIndex = this.pendingRepPlan.replPlanSpecLines.indexOf(element);
    if (rowIndex === -1 || !this.form || !this.form.at(rowIndex)) return;

    const group = this.form.at(rowIndex) as FormGroup;
    const controlName = `${field}_${month}`;
    const control = group.get(controlName) as FormControl | null;
    if (!control) return;

    // If the control is empty on blur, set it to 0 and let the group.valueChanges subscription
    // (which has debounceTime) handle updating dataSource/data model.
    const raw = control.value;
    let valueNum: number;
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      valueNum = trimmed === '' ? 0 : Number(trimmed);
    } else if (typeof raw === 'number') {
      valueNum = raw;
    } else {
      valueNum = 0;
    }
    if (Number.isNaN(valueNum)) valueNum = 0;

    control.setValue(valueNum, { emitEvent: true });
    element.columns[month][field] = valueNum;

    this.reCalculateRepPlan();
  }

  /**
   * Compute the year for a given month number based on the current year and displayed months
   * @param month Month number (1-12)
   * @returns Corresponding year
   */
  getYearForMonth(month: number) {
    let index = this.monthsNumbersToDisplay.findIndex((m) => m === month);
    let year = this.year();
    for (let i = 0; i < index; i++) {
      // If bigger month number found before, it means we passed to next year
      if (this.monthsNumbersToDisplay[i] > month) {
        return year + 1;
      }
    }
    return year;
  }

  /**
   * Expand or collapse the given expandable
   * @param expandable the expandable to expand
   * @protected
   */
  protected toggleExpand(expandable: IExpandable) {
    if (!expandable) return;
    expandable.expanded = expandable.expanded === 'expanded' ? 'collapsed' : 'expanded';
  }

  /**
   * Retrieve result data and conserve display status of pending data (expanded, frozen, ...)
   * @param result The newly fetched data
   * @private
   */
  private restoreDisplayStatusAndPendingData(result: ReplPlan) {
    this.monthsNumbersToDisplay.forEach((month: number) => {
      this.pendingRepPlan.totalLine.columns[month].totalFitment = result.totalLine.columns[month]?.totalFitment;
      this.pendingRepPlan.totalLine.columns[month].totalStock = result.totalLine.columns[month]?.totalStock;
      this.pendingRepPlan.totalLine.columns[month].averageCoverageMonth = result.totalLine.columns[month]?.averageCoverageMonth;
      this.pendingRepPlan.totalLine.columns[month].totalReplenishment = result.totalLine.columns[month]?.totalReplenishment;

      this.pendingRepPlan.replPlanSpecLines.forEach((existingLine, rowIndex) => {
        const resultLine = result.replPlanSpecLines.find((l) => l.tireSpecPk === existingLine.tireSpecPk);
        existingLine.columns[month].stockQty = resultLine.columns[month]?.stockQty ?? 0;
        existingLine.columns[month].coverageMonth = resultLine.columns[month]?.coverageMonth ?? 0;
        existingLine.columns[month].fitmentForecast = resultLine.columns[month]?.fitmentForecast ?? 0;
        existingLine.columns[month].confirmedFitment = resultLine.columns[month]?.confirmedFitment ?? 0;
        existingLine.columns[month].proposedRepl = resultLine.columns[month]?.proposedRepl ?? 0;
        existingLine.columns[month].confirmedRepl = resultLine.columns[month]?.confirmedRepl ?? 0;

        const group = this.form.at(rowIndex) as FormGroup;
        if (group) {
          let fitmentControl = group.get(`confirmedFitment_${month}`) as FormControl | null;
          if (fitmentControl) {
            fitmentControl.setValue(existingLine.columns[month].confirmedFitment, { emitEvent: false });
          }
          let replControl = group.get(`confirmedRepl_${month}`) as FormControl | null;
          if (replControl) {
            replControl.setValue(existingLine.columns[month].confirmedRepl, { emitEvent: false });
          }
        }
      });
    });
  }

  /**
   * Copy all proposed fitments for all spec rows and recalculate plan
   * @private
   */
  public copyAllFitmentsAndRecalculate() {
    this.pendingRepPlan.replPlanSpecLines.forEach((row) => {
      this.copyOneSpecFitments(row);
    });
    this.reCalculateRepPlan();
  }

  /**
   * Copy all proposed fitments for one spec row and recalculate plan
   * @param row the spec row to update
   * @private
   */
  protected copyOneSpecFitmentsAndRecalculate(row: ReplPlanSpecLine) {
    this.copyOneSpecFitments(row);
    this.reCalculateRepPlan();
  }

  /**
   * Copy all proposed fitments for one spec row
   * @param row the spec row to update
   * @private
   */
  private copyOneSpecFitments(row: ReplPlanSpecLine) {
    this.monthsNumbersToDisplay.forEach((month: number) => {
      row.columns[month].confirmedFitment = row.columns[month].fitmentForecast ?? 0;
    });
  }

  /**
   * Calculate appropriated CSS by verifying if coverage follow rep plan's params
   * @param coverageMonth the coverage to verify
   * @protected
   */
  protected getCoverageColorFollowParam(coverageMonth: number): 'danger' | 'warning' | 'valid' | '' {
    if (!this.pendingRepPlan || !this.pendingRepPlan.param) return '';
    if (coverageMonth == this.pendingRepPlan.param.targetCoverage) {
      return 'valid';
    } else if (coverageMonth > this.pendingRepPlan.param.targetCoverage) {
      return 'warning';
    } else {
      return 'danger';
    }
  }

  /**
   * Calculate appropriated CSS by verifying if stockQty follow rep plan's params
   * @param stockQty the stockQty to verify
   * @protected
   */
  protected getStockColorFollowParam(stockQty: number): 'danger' | 'warning' | 'valid' | '' {
    if (!this.pendingRepPlan || !this.pendingRepPlan.param) return '';
    if (stockQty >= this.pendingRepPlan.param.stockMin && stockQty <= this.pendingRepPlan.param.stockMax) {
      return 'valid';
    } else if (stockQty > this.pendingRepPlan.param.stockMax) {
      return 'warning';
    } else {
      return 'danger';
    }
  }

  protected getTotalStockMin(): number {
    if (!this.pendingRepPlan?.replPlanSpecLines?.length) {
      return 0;
    }

    return this.pendingRepPlan.replPlanSpecLines.reduce((total, specLine) => {
      const stockMin = (specLine as any)?.stockMin ?? this.pendingRepPlan?.param?.stockMin ?? 0;
      return total + stockMin;
    }, 0);
  }

  protected getTotalStockMax(): number {
    if (!this.pendingRepPlan?.replPlanSpecLines?.length) {
      return 0;
    }

    return this.pendingRepPlan.replPlanSpecLines.reduce((total, specLine) => {
      const stockMax = (specLine as any)?.stockMax ?? this.pendingRepPlan?.param?.stockMax ?? 0;
      return total + stockMax;
    }, 0);
  }

  protected getTotalStockColor(totalStock: number): 'danger' | 'warning' | 'valid' | '' {
    if (!this.pendingRepPlan) return '';

    const totalMin = this.getTotalStockMin();
    const totalMax = this.getTotalStockMax();

    if (totalStock >= totalMin && totalStock <= totalMax) {
      return 'valid';
    } else if (totalStock > totalMax) {
      return 'warning';
    } else {
      return 'danger';
    }
  }

  /**
   * Say if a value is considered as modified (0 is considered as not modified)
   * @param userValue pending value
   * @param systemValue original value
   * @protected
   */
  protected isModified(userValue: number, systemValue: number) {
    if (userValue === 0) return false;
    return userValue !== systemValue;
  }

  /**
   * Say is given column is in past
   * @param col the column to evaluate
   * @protected
   */
  protected isInPast(col: ReplPlanSpecColumn) {
    return col.year < this.currentYear || (col.year === this.currentYear && col.monthNumber < this.currentMonth);
  }
}
